import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Snackbar,
} from 'ui/mui';
import {
  ArrowBackRounded,
  BrokenImageRounded,
  CheckCircleRounded,
  CloseRounded,
  DoneAllRounded,
  Inventory2Rounded,
  LocalMallRounded,
  ShoppingCartCheckoutRounded,
} from 'ui/icons';
import { supabase } from '../supabaseClient';
import './CardrushProposals.css';

// Status is the only thing coloured: amber waits, green is approved, red is rejected.
const statusMeta = {
  pending: { label: 'Pending', tone: 'warning' },
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'danger' },
  ordered: { label: 'Ordered', tone: 'neutral' },
};

export function CardrushPageTabs({ active }) {
  const navigate = useNavigate();
  const tabs = [
    { key: 'proposals', label: 'Proposals', path: '/cardrush' },
    { key: 'tinder', label: 'Tinder', path: '/cardrush/tinder' },
  ];

  return (
    <div className="rc-segmented cr-tabs">
      {tabs.map((tab) => {
        const isActive = active === tab.key;

        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => navigate(tab.path)}
            className={isActive ? 'is-active' : undefined}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function getUserSession() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return parsed?.accessToken ? parsed : null;
  } catch (error) {
    return null;
  }
}

function normalizeCards(cards) {
  if (!Array.isArray(cards)) return [];

  return cards.map((card) => ({
    ...card,
    approved:
      card?.approved === true
        ? true
        : card?.approved === false
          ? false
          : null,
  }));
}

function normalizeProposal(proposal) {
  if (!proposal) return null;

  return {
    ...proposal,
    cards: normalizeCards(proposal.cards),
  };
}

function formatDateTime(value) {
  if (!value) return 'Unknown date';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown date';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatYen(value) {
  const amount = Number(value) || 0;
  return `¥${new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 }).format(amount)}`;
}

function formatGbp(value) {
  const amount = Number(value) || 0;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function getProposalStats(cards) {
  return normalizeCards(cards).reduce(
    (summary, card) => {
      if (card.approved === true) {
        summary.approved += 1;
        summary.approvedYen += Number(card.price_jpy) || 0;
        summary.approvedGbp += Number(card.price_gbp) || 0;
      } else if (card.approved === false) {
        summary.rejected += 1;
      } else {
        summary.unreviewed += 1;
      }

      return summary;
    },
    {
      approved: 0,
      rejected: 0,
      unreviewed: 0,
      approvedYen: 0,
      approvedGbp: 0,
    }
  );
}

function getFiltersSummary(filters) {
  const activeFilters = [];
  if (filters?.min_price) activeFilters.push(`Min ${formatYen(filters.min_price)}`);
  if (filters?.max_price) activeFilters.push(`Max ${formatYen(filters.max_price)}`);
  if (filters?.count) activeFilters.push(`${filters.count} target`);
  return activeFilters;
}

function StatusChip({ status }) {
  const meta = statusMeta[status] || statusMeta.pending;

  return <Chip label={meta.label} size="small" className="cr-status" data-tone={meta.tone} />;
}

function SummaryPill({ label, value, tone }) {
  return (
    <div className="cr-figure">
      <p className="cr-figure__value" data-tone={tone}>
        {value}
      </p>
      <p className="cr-figure__label rc-stat__label">
        {label}
      </p>
    </div>
  );
}

function ProposalCard({ proposal, onClick }) {
  return (
    <button type="button" onClick={onClick} className="rc-tile cr-proposal">
      <div className="cr-proposal__head">
        <p className="cr-proposal__date">
          {formatDateTime(proposal.created_at)}
        </p>
        <StatusChip status={proposal.status} />
      </div>

      <p className="cr-proposal__filters">
        {getFiltersSummary(proposal.filters).join(' · ') || 'No filters recorded'}
      </p>

      <div className="cr-figures">
        <SummaryPill label="Cards" value={proposal.total_cards || 0} />
        <SummaryPill label="Total Yen" value={formatYen(proposal.total_yen)} />
        <SummaryPill label="Total GBP" value={formatGbp(proposal.total_gbp)} />
        <SummaryPill label="Status" value={statusMeta[proposal.status]?.label || 'Pending'} tone={statusMeta[proposal.status]?.tone || 'warning'} />
      </div>
    </button>
  );
}

export default function CardrushProposals() {
  const navigate = useNavigate();
  const session = getUserSession();

  const [listLoading, setListLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const showToast = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      severity,
      message,
    });
  };

  const loadProposals = async () => {
    setListLoading(true);

    try {
      const { data, error } = await supabase
        .from('cardrush_proposals')
        .select('id, created_at, status, filters, total_cards, total_yen, total_gbp')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProposals(Array.isArray(data) ? data : []);
    } catch (error) {
      showToast(error.message || 'Failed to load Cardrush proposals.', 'error');
    } finally {
      setListLoading(false);
    }
  };

  const loadProposalDetail = async (proposalId) => {
    if (!proposalId) return;

    setDetailLoading(true);
    setImageErrors({});

    try {
      const { data, error } = await supabase
        .from('cardrush_proposals')
        .select('*')
        .eq('id', proposalId)
        .single();

      if (error) throw error;
      setSelectedProposal(normalizeProposal(data));
    } catch (error) {
      showToast(error.message || 'Failed to load proposal details.', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (session) {
      loadProposals();
    }
  }, []);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const updateCards = async (nextCards, successMessage) => {
    if (!selectedProposal?.id) return;

    setSaving(true);

    try {
      const { data, error } = await supabase
        .from('cardrush_proposals')
        .update({ cards: nextCards })
        .eq('id', selectedProposal.id)
        .select('*')
        .single();

      if (error) throw error;
      setSelectedProposal(normalizeProposal(data));
      if (successMessage) {
        showToast(successMessage);
      }
    } catch (error) {
      showToast(error.message || 'Failed to save review state.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReview = async (index, approved) => {
    const cards = normalizeCards(selectedProposal?.cards);
    if (!cards[index]) return;
    if (cards[index].approved === approved) return;

    const nextCards = cards.map((card, cardIndex) =>
      cardIndex === index
        ? {
            ...card,
            approved,
          }
        : card
    );

    await updateCards(nextCards);
  };

  const handleApproveAllUnreviewed = async () => {
    const cards = normalizeCards(selectedProposal?.cards);
    if (!cards.some((card) => card.approved === null)) return;

    const nextCards = cards.map((card) =>
      card.approved === null
        ? {
            ...card,
            approved: true,
          }
        : card
    );

    await updateCards(nextCards, 'All unreviewed cards approved.');
  };

  const handleConfirmOrder = async () => {
    if (!selectedProposal?.id) return;

    setConfirming(true);

    try {
      const response = await fetch('/api/cardrush-confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.accessToken}`,
        },
        body: JSON.stringify({
          proposal_id: selectedProposal.id,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('user');
          navigate('/login');
          return;
        }

        if (response.status === 403) {
          navigate('/');
          return;
        }

        throw new Error(payload?.details || payload?.message || `HTTP ${response.status}`);
      }

      setSelectedProposal((current) => (current ? { ...current, status: 'approved' } : current));
      setProposals((current) =>
        current.map((proposal) =>
          proposal.id === selectedProposal.id
            ? {
                ...proposal,
                status: 'approved',
              }
            : proposal
        )
      );
      setConfirmOpen(false);
      showToast('Proposal confirmed for ordering.');
    } catch (error) {
      showToast(error.message || 'Failed to confirm order.', 'error');
    } finally {
      setConfirming(false);
    }
  };

  const proposalCards = normalizeCards(selectedProposal?.cards);
  const stats = getProposalStats(proposalCards);
  const hasApprovedCards = stats.approved > 0;
  const filtersSummary = getFiltersSummary(selectedProposal?.filters);

  return (
    <div className="rc-page cr-page">
      <header className="cr-header">
        {!selectedProposal ? (
          <div className="cr-header__text">
            <p className="rc-eyebrow">Buying</p>
            <h1 className="rc-page-title">
              Cardrush Order Proposals
            </h1>
            <p className="rc-page-description">
              Review generated buy proposals, approve individual cards, and confirm the final order handoff for staff fulfilment.
            </p>
          </div>
        ) : (
          <div className="cr-header__text">
            <Button
              type="button"
              startIcon={<ArrowBackRounded />}
              onClick={() => setSelectedProposal(null)}
              variant="text"
              className="cr-back"
            >
              Back
            </Button>
            <p className="rc-eyebrow">Buying</p>
            <h1 className="rc-page-title">
              Proposal from {formatDateTime(selectedProposal.created_at)}
            </h1>
            <p className="rc-page-description">
              Review each recommended card, then confirm the approved subset for ordering.
            </p>
          </div>
        )}
        <div className="cr-header__actions">
          <CardrushPageTabs active="proposals" />
          {!selectedProposal ? (
            <Chip
              icon={<Inventory2Rounded size={16} />}
              label={`${proposals.length} proposal${proposals.length === 1 ? '' : 's'}`}
              className="cr-count"
            />
          ) : (
            <StatusChip status={selectedProposal.status} />
          )}
        </div>
      </header>

      {!selectedProposal ? (
        <>
          <Divider className="cr-rule" />

          {listLoading ? (
            <div className="cr-loading">
              <CircularProgress size={24} />
            </div>
          ) : proposals.length === 0 ? (
            <div className="cr-empty">
              <div className="cr-empty__inner">
                <span className="rc-empty__icon"><LocalMallRounded size={20} /></span>
                <p className="rc-empty__title">
                  No Cardrush proposals found
                </p>
                <p className="rc-empty__body">
                  Run the builder script first, then refresh this dashboard.
                </p>
              </div>
            </div>
          ) : (
            <div className="cr-proposals">
              {proposals.map((proposal) => (
                <ProposalCard
                  key={proposal.id}
                  proposal={proposal}
                  onClick={() => loadProposalDetail(proposal.id)}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="cr-figures cr-figures--detail">
            <SummaryPill label="Total Cards" value={selectedProposal.total_cards || 0} />
            <SummaryPill label="Total Yen" value={formatYen(selectedProposal.total_yen)} />
            <SummaryPill label="Total GBP" value={formatGbp(selectedProposal.total_gbp)} />
            <SummaryPill label="Filters" value={filtersSummary.length || 0} />
          </div>

          <div className="cr-filters">
            {filtersSummary.length ? (
              filtersSummary.map((filterLabel) => (
                <Chip
                  key={filterLabel}
                  label={filterLabel}
                  className="cr-filter"
                />
              ))
            ) : (
              <Chip
                label="No filters recorded"
                className="cr-filter cr-filter--none"
              />
            )}
          </div>

          <div className="cr-figures cr-figures--review">
            <SummaryPill label="Approved" value={stats.approved} tone="success" />
            <SummaryPill label="Rejected" value={stats.rejected} tone="danger" />
            <SummaryPill label="Unreviewed" value={stats.unreviewed} tone="warning" />
          </div>

          <Divider className="cr-rule" />

          {detailLoading ? (
            <div className="cr-loading">
              <CircularProgress size={24} />
            </div>
          ) : (
            <div className="cr-cards">
              {proposalCards.map((card, index) => {
                const cardKey = card.pid || index;
                const reviewState =
                  card.approved === true ? 'approved' : card.approved === false ? 'rejected' : 'pending';
                const imageFailed = imageErrors[cardKey];

                return (
                  <div
                    key={`${selectedProposal.id}-${cardKey}`}
                    className="cr-card"
                    data-review={reviewState}
                  >
                    <div className="cr-card__media">
                      {!imageFailed && card.image_url ? (
                        <img
                          src={card.image_url}
                          alt={card.name || 'Card image'}
                          onError={() =>
                            setImageErrors((current) => ({
                              ...current,
                              [cardKey]: true,
                            }))
                          }
                        />
                      ) : (
                        <div className="cr-missing">
                          <div className="cr-missing__inner">
                            <BrokenImageRounded size={32} />
                            <p>Image unavailable</p>
                          </div>
                        </div>
                      )}

                      {reviewState !== 'pending' && (
                        <div className="cr-card__mark" data-review={reviewState}>
                          {reviewState === 'approved' ? (
                            <CheckCircleRounded size={20} />
                          ) : (
                            <CloseRounded size={18} />
                          )}
                        </div>
                      )}
                    </div>

                    <div className="cr-card__body">
                      <div className="cr-card__meta">
                        <Chip
                          label={card.rarity || 'Unknown'}
                          size="small"
                          className="cr-rarity"
                        />
                        <p className="cr-card__pid">
                          PID {card.pid || 'N/A'}
                        </p>
                      </div>

                      <p className="cr-card__name">
                        {card.name || 'Unnamed card'}
                      </p>

                      <p className="cr-card__price">
                        <span className="cr-card__yen">{formatYen(card.price_jpy)}</span> <span className="cr-card__gbp">/ {formatGbp(card.price_gbp)}</span>
                      </p>

                      <p className="cr-card__line">
                        Qty recommended: <span className="cr-card__qty">×{card.recommended_qty || 0}</span>
                      </p>

                      <p className="cr-card__line cr-card__line--muted">
                        {card.times_bought
                          ? `Bought ${card.times_bought}× (${card.units_bought_total || 0} total)`
                          : 'New card'}
                      </p>

                      {card.last_price_jpy ? (
                        <p className="cr-card__line cr-card__line--muted">
                          Last seen at {formatYen(card.last_price_jpy)}
                        </p>
                      ) : null}

                      <div className="cr-card__actions">
                        <Button
                          type="button"
                          fullWidth
                          variant="outlined"
                          disabled={saving}
                          onClick={() => handleReview(index, true)}
                          className="cr-choice cr-choice--approve"
                          data-chosen={card.approved === true ? 'true' : undefined}
                        >
                          Approve
                        </Button>
                        <Button
                          type="button"
                          fullWidth
                          variant="outlined"
                          disabled={saving}
                          onClick={() => handleReview(index, false)}
                          className="cr-choice cr-choice--reject"
                          data-chosen={card.approved === false ? 'true' : undefined}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="cr-summary-bar">
            <div className="cr-summary-bar__text">
              <p className="cr-summary-bar__total">
                {stats.approved} approved · {formatYen(stats.approvedYen)} (~{formatGbp(stats.approvedGbp)})
              </p>
              <p className="cr-summary-bar__sub">
                {stats.rejected} rejected · {stats.unreviewed} unreviewed
              </p>
            </div>

            <div className="cr-summary-bar__actions">
              <Button
                type="button"
                variant="outlined"
                startIcon={<DoneAllRounded />}
                disabled={saving || detailLoading || stats.unreviewed === 0}
                onClick={handleApproveAllUnreviewed}
              >
                Approve All Unreviewed
              </Button>
              <Button
                type="button"
                variant="contained"
                startIcon={<ShoppingCartCheckoutRounded />}
                disabled={
                  saving ||
                  confirming ||
                  !hasApprovedCards ||
                  selectedProposal.status === 'approved' ||
                  selectedProposal.status === 'ordered'
                }
                onClick={() => setConfirmOpen(true)}
              >
                Confirm Order
              </Button>
            </div>
          </div>
        </>
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => !confirming && setConfirmOpen(false)}
      >
        <DialogTitle>
          <span className="cr-dialog-title">Confirm Cardrush Order</span>
        </DialogTitle>
        <DialogContent>
          <p className="cr-dialog-text">
            This will mark the proposal as approved and hand it off for the separate order execution step.
          </p>
          <div className="cr-dialog-summary">
            <p className="cr-dialog-summary__count">
              {stats.approved} approved card{stats.approved === 1 ? '' : 's'}
            </p>
            <p className="cr-dialog-summary__total">
              <span className="cr-dialog-summary__yen">{formatYen(stats.approvedYen)}</span> <span className="cr-dialog-summary__gbp">(~{formatGbp(stats.approvedGbp)})</span>
            </p>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            variant="outlined"
            onClick={() => setConfirmOpen(false)}
            disabled={confirming}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirmOrder}
            disabled={confirming}
            variant="contained"
          >
            {confirming ? 'Confirming...' : 'Confirm Order'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
